import * as React from 'react';
import { LOCAL_AVE_SWITCH_EVENT, userSession } from 'state/session';

/**
 * AVS-4389 — when the dealer switches which local AVE device is streaming, this hook forces the
 * `<avp-3dws-deck>` element's `active` attribute false long enough for the old device to tear
 * down, then flips it back true so the deck reconnects to the new device. The element itself is
 * never unmounted/remounted.
 *
 * Real-hardware testing showed the deck discards its custom signaling config during deactivation
 * — reactivating without re-applying it makes the deck fall back to the CLOUD streaming path
 * instead of the local device. `onReactivate` exists for exactly that: the caller re-issues
 * `setConnection(...)` through it, called synchronously right before `active` flips back to true.
 *
 * Re-activating too early is also dangerous on its own: a real device log showed the hardware's
 * own teardown request (AVEEndSession) taking up to its full 30s timeout budget. Flipping `active`
 * back to true before that finishes makes the new session fight the still-tearing-down old one for
 * the same hardware, and every request then fails. LOCAL_AVE_SWITCH_TIMEOUT_MS exists to respect
 * that budget — see its own comment for the caveat on the exact value.
 */

/**
 * How long we wait for the deck to confirm teardown (before forcing `active` back on anyway) or
 * to confirm a new connection (before giving up on the switch entirely). Chosen to comfortably
 * clear the one observed real-hardware AVEEndSession timeout (~30s) — not a confirmed-safe value,
 * and it needs live-hardware confirmation after this change lands.
 */
export const LOCAL_AVE_SWITCH_TIMEOUT_MS = 45_000;

/** Delay used once the deck HAS confirmed teardown — 0ms is enough, it only needs to be a macrotask. */
export const LOCAL_AVE_SWITCH_FAST_REACTIVATE_DELAY_MS = 0;

interface LocalAveSwitchEventPayload {
  deviceId?: string;
}

export interface IUseLocalAveDeviceSwitchResult {
  /** ANDed (never OR'd/replacing) into `shouldBeActive` in the consuming component. */
  forceInactive: boolean;
  /** True from the switch event until the deck reports `connected`, or the timeout elapses. */
  switchInFlight: boolean;
  /** Mirror of `switchInFlight`, read synchronously by the `disconnected`/`exception` listeners. */
  switchInFlightRef: React.RefObject<boolean>;
  /** Call from the `disconnected` listener when a deliberate teardown is observed. Stable identity. */
  notifyDeckTornDown: () => void;
  /** Call from the `connected` listener once the new device has handshaked. Stable identity. */
  notifyDeckConnected: () => void;
}

interface PendingReactivation {
  forSwitchId: number;
  cancel: () => void;
}

export function useLocalAveDeviceSwitch(
  localAveName: string | undefined,
  onReactivate?: () => void,
  onSwitchTimedOut?: () => void
): IUseLocalAveDeviceSwitchResult {
  const localAveNameRef = React.useRef(localAveName);
  // Read through refs (not useCallback dependencies) so the caller can pass fresh closures
  // every render without retriggering the effects below.
  const onReactivateRef = React.useRef(onReactivate);
  const onSwitchTimedOutRef = React.useRef(onSwitchTimedOut);

  // pendingSwitchId counts each authorised switch. A plain boolean can't tell a stale pending
  // timer (from a switch that got superseded) apart from the current one — an id can.
  const [pendingSwitchId, setPendingSwitchId] = React.useState(0);
  const [lastReactivatedSwitchId, setLastReactivatedSwitchId] = React.useState(0);
  const [lastSettledSwitchId, setLastSettledSwitchId] = React.useState(0);

  const pendingSwitchIdRef = React.useRef(0);
  const switchInFlightRef = React.useRef(false);
  const tornDownForSwitchIdRef = React.useRef(0);
  const pendingReactivationRef = React.useRef<PendingReactivation | null>(null);

  React.useEffect(() => {
    localAveNameRef.current = localAveName;
    onReactivateRef.current = onReactivate;
    onSwitchTimedOutRef.current = onSwitchTimedOut;
  }, [localAveName, onReactivate, onSwitchTimedOut]);

  React.useEffect(() => {
    const off = userSession().on(LOCAL_AVE_SWITCH_EVENT, (payload: LocalAveSwitchEventPayload) => {
      const currentLocalAveName = localAveNameRef.current;
      // Cloud streaming (no localAveName) and a stale/foreign device id are both ignored — the
      // hook must react only to a switch that authorises restarting THIS element's own device.
      if (!currentLocalAveName) return;
      if (payload?.deviceId !== currentLocalAveName) return;

      const nextId = pendingSwitchIdRef.current + 1;
      // Written synchronously here (not via setPendingSwitchId's updater): a custom element can
      // dispatch `disconnected` synchronously as a direct result of the DOM attribute write that
      // follows this event, in the same React commit, before any effect has run — the listener
      // reading this ref must already see the new value at that point.
      pendingSwitchIdRef.current = nextId;
      switchInFlightRef.current = true;
      setPendingSwitchId(nextId);
    });
    return off;
  }, []);

  const forceInactive = pendingSwitchId > lastReactivatedSwitchId;
  const switchInFlight = pendingSwitchId > lastSettledSwitchId;

  React.useLayoutEffect(() => {
    switchInFlightRef.current = switchInFlight;
  }, [switchInFlight]);

  const armReactivation = React.useCallback((id: number, delay: number) => {
    pendingReactivationRef.current?.cancel();
    const timer = setTimeout(() => {
      // Must run before the state update below writes `active="true"` to the DOM — the deck
      // drops its signaling config on deactivation, so it has to be re-applied first or the
      // deck falls back to cloud streaming instead of reconnecting to the local device.
      onReactivateRef.current?.();
      setLastReactivatedSwitchId(id);
    }, delay);
    pendingReactivationRef.current = { forSwitchId: id, cancel: () => clearTimeout(timer) };
  }, []);

  // Reactivates `active` once the old device is presumed torn down: immediately if
  // notifyDeckTornDown already fired for this switch, otherwise once the timeout elapses.
  React.useEffect(() => {
    if (pendingSwitchId === 0 || pendingSwitchId === lastReactivatedSwitchId) return;

    const delay =
      tornDownForSwitchIdRef.current === pendingSwitchId
        ? LOCAL_AVE_SWITCH_FAST_REACTIVATE_DELAY_MS
        : LOCAL_AVE_SWITCH_TIMEOUT_MS;
    armReactivation(pendingSwitchId, delay);
    return () => pendingReactivationRef.current?.cancel();
  }, [pendingSwitchId, lastReactivatedSwitchId, armReactivation]);

  // Independent of reactivation: only notifyDeckConnected or this timeout ends the in-flight
  // window, so a fast (acked) reactivation doesn't prematurely dismiss the loading screen.
  // PR review (AVS-4389): if notifyDeckConnected never fires, the new device never came up —
  // surface that via onSwitchTimedOut rather than leaving the caller with a dead canvas and no
  // signal. A superseding switch or notifyDeckConnected reschedules/cancels this effect (deps
  // below) before the timer fires, so this only reports a genuine dead end.
  React.useEffect(() => {
    if (pendingSwitchId === 0 || pendingSwitchId === lastSettledSwitchId) return;

    const timer = setTimeout(() => {
      setLastSettledSwitchId(pendingSwitchId);
      onSwitchTimedOutRef.current?.();
    }, LOCAL_AVE_SWITCH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingSwitchId, lastSettledSwitchId]);

  // A device unbind mid-switch (e.g. endSession()) must never leave `active` pinned false.
  React.useEffect(() => {
    if (!localAveName && pendingSwitchId > 0) {
      setLastReactivatedSwitchId(pendingSwitchId);
      setLastSettledSwitchId(pendingSwitchId);
    }
  }, [localAveName, pendingSwitchId]);

  const notifyDeckTornDown = React.useCallback(() => {
    const id = pendingSwitchIdRef.current;
    if (id === 0) return;
    tornDownForSwitchIdRef.current = id;
    // If the reactivation effect already armed the (long) timeout for this switch, reschedule it
    // now — otherwise the effect will pick up tornDownForSwitchIdRef itself when it runs.
    if (pendingReactivationRef.current?.forSwitchId === id) {
      armReactivation(id, LOCAL_AVE_SWITCH_FAST_REACTIVATE_DELAY_MS);
    }
  }, [armReactivation]);

  const notifyDeckConnected = React.useCallback(() => {
    const id = pendingSwitchIdRef.current;
    if (id === 0) return;
    setLastSettledSwitchId(id);
  }, []);

  return {
    forceInactive,
    switchInFlight,
    switchInFlightRef,
    notifyDeckTornDown,
    notifyDeckConnected,
  };
}
